import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  Storefront as StoreIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import moment from 'moment-jalaali';
import { toast } from 'react-toastify';
import ProtectedRoute from '../components/common/ProtectedRoute';
import ResponsiveTable, { Column } from '../components/common/ResponsiveTable';
import JalaliDateField from '../components/common/JalaliDateField';
import financeLedgerService, {
  StatementRow,
  UserStatement,
  StoreStatement,
  StoreBalanceExplain,
  StoreFundEvents,
  TYPE_LABELS,
  GATEWAY_LABELS,
  GATEWAY_COLORS,
  STATUS_LABELS,
  DIRECTION_LABELS,
} from '../services/financeLedgerService';
import './EntityManagement.css';

const toPersianDigits = (s: string | number): string =>
  String(s).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);

const formatToman = (n: number | null | undefined): string => {
  if (n == null || !isFinite(Number(n))) return '—';
  return `${new Intl.NumberFormat('fa-IR').format(Number(n))} تومان`;
};

const formatJalali = (iso?: string | null): string => {
  if (!iso) return '—';
  try {
    return toPersianDigits(moment(iso).format('jYYYY/jMM/jDD HH:mm'));
  } catch {
    return '—';
  }
};

const jalaliToISO = (value: string, endOfDay = false): string | undefined => {
  const v = value.trim();
  if (!v) return undefined;
  const m = moment(v, 'jYYYY/jMM/jDD');
  if (!m.isValid()) return undefined;
  if (endOfDay) m.endOf('day');
  else m.startOf('day');
  return m.toISOString();
};

interface PartyOption {
  id: string;
  name: string;
  phoneNumber?: string;
  email?: string;
}

const FinanceStatements: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [kind, setKind] = useState<'user' | 'store'>(searchParams.get('storeId') ? 'store' : 'user');
  const [options, setOptions] = useState<PartyOption[]>([]);
  const [optLoading, setOptLoading] = useState(false);
  const [party, setParty] = useState<PartyOption | null>(null);
  const [fromJalali, setFromJalali] = useState('');
  const [toJalali, setToJalali] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [userStmt, setUserStmt] = useState<UserStatement | null>(null);
  const [storeStmt, setStoreStmt] = useState<StoreStatement | null>(null);
  // 🧾 «چرا پول فروشنده قابل برداشت نیست؟» + دفتر رویدادهای مالی فروشنده
  const [storeExplain, setStoreExplain] = useState<StoreBalanceExplain | null>(null);
  const [storeEvents, setStoreEvents] = useState<StoreFundEvents | null>(null);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsType, setEventsType] = useState('');
  const [eventsSearch, setEventsSearch] = useState('');
  const [explainBucket, setExplainBucket] = useState('all');
  const [explainStoreId, setExplainStoreId] = useState('');
  const BUCKET_UI: Record<string, { label: string; color: string }> = {
    available: { label: 'قابل برداشت', color: '#2e7d32' }, escrow: { label: 'در دورهٔ گارانتی', color: '#b45309' }, awaiting_delivery: { label: 'در انتظار تحویل', color: '#c62828' },
    blocked: { label: 'مسدود', color: '#6a1b9a' }, obligation: { label: 'قفل بابت بازپرداخت', color: '#d32f2f' }, excluded: { label: 'مسترد/لغو', color: '#6e6e73' },
  };
  const EFFECT_UI: Record<string, { label: string; color: string; sign: string }> = {
    credit: { label: 'افزایش', color: '#2e7d32', sign: '+' }, debit: { label: 'کسر', color: '#c62828', sign: '−' }, lock: { label: 'قفل', color: '#d32f2f', sign: '🔒' }, unlock: { label: 'آزاد', color: '#1565c0', sign: '🔓' }, info: { label: 'اطلاع', color: '#6e6e73', sign: '' },
  };
  const EVENT_GROUPS: { value: string; label: string }[] = [
    { value: '', label: 'همهٔ رویدادها' },
    { value: 'sale_paid,item_delivered', label: 'فروش و تحویل' },
    { value: 'dispute_opened,dispute_closed', label: 'اختلاف' },
    { value: 'item_refunded,item_cancelled,refund_deferred,refund_obligation_paid,refund_obligation_forced,refund_obligation_cancelled', label: 'استرداد و تعهد' },
    { value: 'withdrawal_requested,withdrawal_verified,withdrawal_auto_approved,withdrawal_approved,withdrawal_processing,withdrawal_payout_submitted,withdrawal_payout_queued,withdrawal_payout_failed,withdrawal_completed,withdrawal_rejected,withdrawal_failed,withdrawal_cancelled,withdrawal_expired', label: 'برداشت' },
    { value: 'penalty,adjustment,inspection_fee_earned,inspection_fee_refunded', label: 'جریمه/بازرسی' },
  ];
  const loadStoreExplain = useCallback(async (id: string) => {
    try { setStoreExplain(await financeLedgerService.getStoreExplain(id)); } catch { setStoreExplain(null); }
  }, []);
  const loadStoreEvents = useCallback(async (id: string, p: number, type: string, search: string) => {
    try { setStoreEvents(await financeLedgerService.getStoreFundEvents(id, { page: p, limit: 20, type: type || undefined, search: search || undefined })); } catch { setStoreEvents(null); }
  }, []);
  useEffect(() => {
    if (explainStoreId) loadStoreEvents(explainStoreId, eventsPage, eventsType, eventsSearch);
  }, [explainStoreId, eventsPage, eventsType, eventsSearch, loadStoreEvents]);

  const fetchStatement = useCallback(async (
    k: 'user' | 'store',
    id: string,
    p: number,
    rpp: number,
    fromJ: string,
    toJ: string,
  ) => {
    setLoading(true);
    try {
      const params = {
        from: jalaliToISO(fromJ),
        to: jalaliToISO(toJ, true),
        page: p + 1,
        limit: rpp,
      };
      if (k === 'user') {
        const data = await financeLedgerService.getUserStatement(id, params);
        setUserStmt(data);
        setStoreStmt(null);
      } else {
        const data = await financeLedgerService.getStoreStatement(id, params);
        setStoreStmt(data);
        setUserStmt(null);
        setExplainStoreId(id);
        setEventsPage(1);
        loadStoreExplain(id);
      }
    } catch {
      toast.error('خطا در دریافت گردش حساب');
    } finally {
      setLoading(false);
    }
  }, []);

  // deep-link: ?userId= / ?storeId=
  useEffect(() => {
    const userId = searchParams.get('userId');
    const storeId = searchParams.get('storeId');
    if (userId) {
      setKind('user');
      setParty({ id: userId, name: '...' });
      fetchStatement('user', userId, 0, rowsPerPage, '', '');
    } else if (storeId) {
      setKind('store');
      setParty({ id: storeId, name: '...' });
      fetchStatement('store', storeId, 0, rowsPerPage, '', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refresh resolved party name from statement payload
  useEffect(() => {
    if (userStmt?.user && party && party.name === '...') {
      setParty({ id: userStmt.user.id, name: userStmt.user.name, phoneNumber: userStmt.user.phoneNumber });
    }
    if (storeStmt?.store && party && party.name === '...') {
      setParty({ id: storeStmt.store.id, name: storeStmt.store.name });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStmt, storeStmt]);

  const searchOptions = async (q: string) => {
    if (!q || q.trim().length < 2) {
      setOptions([]);
      return;
    }
    setOptLoading(true);
    try {
      const res = await financeLedgerService.searchParties(kind, q.trim());
      setOptions(res || []);
    } catch {
      setOptions([]);
    } finally {
      setOptLoading(false);
    }
  };

  const load = (p = 0, rpp = rowsPerPage) => {
    if (!party) {
      toast.info(kind === 'user' ? 'ابتدا یک کاربر انتخاب کنید' : 'ابتدا یک فروشگاه انتخاب کنید');
      return;
    }
    setPage(p);
    fetchStatement(kind, party.id, p, rpp, fromJalali, toJalali);
  };

  const stmt = kind === 'user' ? userStmt : storeStmt;

  const columns: Column<StatementRow>[] = [
    { id: 'date', label: 'تاریخ', priority: 1, format: (v) => <span style={{ whiteSpace: 'nowrap' }}>{formatJalali(v)}</span> },
    { id: 'type', label: 'نوع', priority: 2, format: (v: string) => TYPE_LABELS[v] || v },
    {
      id: 'gateway' as any,
      label: 'درگاه',
      priority: 3,
      format: (_v: any, row: any) => {
        const key = row.gateway || row.orderGateway;
        if (!key) return <span style={{ color: 'var(--color-text-disabled)' }}>—</span>;
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            <Chip size="small" variant="outlined" label={GATEWAY_LABELS[key] || key} sx={{ borderColor: GATEWAY_COLORS[key], color: GATEWAY_COLORS[key], fontWeight: 700 }} />
            {(row.orderSplit || (!row.gateway && row.orderGateway)) && (
              <span style={{ display: 'block', fontSize: 10, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                {row.orderSplit ? 'سفارش پرداخت ترکیبی' : 'درگاهِ سفارش'}
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: 'direction',
      label: 'جهت',
      priority: 4,
      format: (v) => (
        <span style={{
          color: v === 'credit' ? 'var(--color-success-dark)' : v === 'debit' ? 'var(--color-error-dark)' : 'var(--color-text-secondary)',
          fontWeight: 700,
        }}>
          {DIRECTION_LABELS[v as 'credit' | 'debit' | 'neutral'] || v}
        </span>
      ),
    },
    {
      id: 'amount',
      label: 'مبلغ',
      priority: 1,
      format: (v: number, row) => (
        <span style={{
          fontWeight: 700,
          whiteSpace: 'nowrap',
          color: row.direction === 'credit' ? 'var(--color-success-dark)' : row.direction === 'debit' ? 'var(--color-error-dark)' : undefined,
        }}>
          {row.direction === 'debit' ? '−' : row.direction === 'credit' ? '+' : ''}{formatToman(v)}
        </span>
      ),
    },
    {
      id: 'balanceAfter',
      label: 'مانده پس از سند',
      priority: 3,
      format: (v: number) => <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{formatToman(v)}</span>,
    },
    {
      id: 'status',
      label: 'وضعیت',
      priority: 6,
      hideOnMobile: true,
      format: (v: string) => <Chip size="small" variant="outlined" label={STATUS_LABELS[v] || v} />,
    },
    { id: 'description', label: 'شرح', priority: 5, detail: true, format: (v: string) => v || '—' },
    {
      id: 'refId',
      label: 'کد پیگیری',
      hideOnMobile: true,
      detail: true,
      format: (v: string | null) => (v ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> : '—'),
    },
  ];

  const snapshotCards = kind === 'user' && userStmt?.snapshot
    ? [
        { title: 'موجودی فعلی', value: userStmt.snapshot.balance, color: 'green' },
        { title: 'مبالغ قفل‌شده', value: userStmt.snapshot.lockedBalance, color: 'orange' },
        { title: 'رزرو برداشت', value: userStmt.snapshot.blockedForWithdrawal, color: 'red' },
        { title: 'مجموع برداشت', value: userStmt.snapshot.totalWithdrawn, color: 'blue' },
      ]
    : kind === 'store' && storeStmt?.snapshot
      ? [
          { title: 'کل درآمد (لحظه‌ای)', value: storeStmt.snapshot.totalEarnings, color: 'green' },
          { title: 'در انتظار (گارانتی + تحویل)', value: storeStmt.snapshot.pendingBalance, color: 'orange' },
          { title: 'قابل برداشت', value: storeStmt.snapshot.availableBalance, color: 'blue' },
          { title: 'مجموع برداشت', value: storeStmt.snapshot.totalWithdrawn, color: 'purple' },
          ...(storeStmt.snapshot.blockedAmount ? [{ title: 'مسدود (اختلاف)', value: storeStmt.snapshot.blockedAmount, color: 'red' }] : []),
          ...(storeStmt.snapshot.debt ? [{ title: 'بدهکار به پلتفرم', value: storeStmt.snapshot.debt, color: 'red' }] : []),
        ]
      : [];

  return (
    <ProtectedRoute requiredPermissions={['admin_finance_statement']}>
      <Helmet>
        <title>گردش حساب</title>
      </Helmet>

      <div className="entity-page" dir="rtl">
        <div className="entity-header">
          <div>
            <h1 className="entity-title">
              <WalletIcon style={{ verticalAlign: 'middle', marginLeft: 8 }} />
              گردش حساب
            </h1>
            <p className="entity-subtitle">صورتحساب کامل کاربر یا فروشگاه با مانده لحظه‌ای هر سند</p>
          </div>
          <div className="header-actions">
            <button
              className="header-btn refresh"
              onClick={() => load(page)}
              disabled={loading || !party}
              title="بروزرسانی"
            >
              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </button>
          </div>
        </div>

        {/* Picker */}
        <div className="edit-card" style={{ marginBottom: 16 }}>
          <div className="edit-card-body">
            <Tabs
              value={kind}
              onChange={(_e, v) => {
                setKind(v);
                setParty(null);
                setOptions([]);
                setUserStmt(null);
                setStoreStmt(null);
              }}
              sx={{ mb: 2 }}
            >
              <Tab icon={<PersonIcon />} iconPosition="start" label="کاربر" value="user" />
              <Tab icon={<StoreIcon />} iconPosition="start" label="فروشگاه" value="store" />
            </Tabs>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Autocomplete<PartyOption>
                sx={{ minWidth: 280, flex: 1, maxWidth: 420 }}
                size="small"
                options={options}
                value={party}
                loading={optLoading}
                filterOptions={(x) => x}
                getOptionLabel={(o) =>
                  o.phoneNumber ? `${o.name} (${toPersianDigits(o.phoneNumber)})` : o.name
                }
                isOptionEqualToValue={(o, v) => o.id === v.id}
                onInputChange={(_e, v, reason) => {
                  if (reason === 'input') searchOptions(v);
                }}
                onChange={(_e, v) => setParty(v)}
                noOptionsText="موردی پیدا نشد — حداقل ۲ حرف تایپ کنید"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={kind === 'user' ? 'جستجوی کاربر (موبایل / نام / ایمیل)' : 'جستجوی فروشگاه (نام)'}
                  />
                )}
              />
              <JalaliDateField label="از تاریخ (شمسی)" value={fromJalali} onChange={setFromJalali} />
              <JalaliDateField label="تا تاریخ (شمسی)" value={toJalali} onChange={setToJalali} />
              <Button variant="contained" onClick={() => load(0)} disabled={loading}>
                نمایش گردش حساب
              </Button>
            </Box>
          </div>
        </div>

        {stmt && (
          <>
            {/* Snapshot cards */}
            {snapshotCards.length > 0 && (
              <div className="entity-stats">
                {snapshotCards.map((c, i) => (
                  <div key={i} className="stat-card">
                    <div className={`stat-icon ${c.color}`}><WalletIcon /></div>
                    <div className="stat-content">
                      <div className="stat-label">{c.title}</div>
                      <div className="stat-value">{formatToman(c.value)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Opening / closing balance + totals */}
            <div className="edit-card" style={{ marginBottom: 16 }}>
              <div className="edit-card-header">خلاصه دوره</div>
              <div className="edit-card-body">
                <table className="entity-table">
                  <tbody>
                    <tr>
                      <td>مانده اول دوره</td>
                      <td style={{ fontWeight: 700 }}>{formatToman(stmt.openingBalance)}</td>
                      <td>جمع بستانکار دوره</td>
                      <td style={{ color: 'var(--color-success-dark)', fontWeight: 700 }}>{formatToman(stmt.totals.credit)}</td>
                    </tr>
                    <tr>
                      <td>مانده پایان دوره</td>
                      <td style={{ fontWeight: 700 }}>{formatToman(stmt.closingBalance)}</td>
                      <td>جمع بدهکار دوره</td>
                      <td style={{ color: 'var(--color-error-dark)', fontWeight: 700 }}>{formatToman(stmt.totals.debit)}</td>
                    </tr>
                    <tr>
                      <td>تعداد اسناد دوره</td>
                      <td>{new Intl.NumberFormat('fa-IR').format(stmt.totals.count)}</td>
                      <td>تغییر خالص دوره</td>
                      <td style={{ fontWeight: 700 }}>{formatToman(stmt.totals.credit - stmt.totals.debit)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Statement rows */}
            <ResponsiveTable<StatementRow>
              columns={columns}
              data={stmt.rows}
              keyField="id"
              loading={loading}
              emptyMessage="در این بازه تراکنشی ثبت نشده است"
              pagination
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={stmt.total}
              onPageChange={(p) => load(p)}
              onRowsPerPageChange={(rpp) => { setRowsPerPage(rpp); load(0, rpp); }}
              rowsPerPageOptions={[25, 50, 100]}
              stickyHeader
            />

            {/* Store withdrawals */}
            {kind === 'store' && storeStmt && storeStmt.withdrawals.length > 0 && (
              <div className="edit-card" style={{ marginTop: 16 }}>
                <div className="edit-card-header">درخواست‌های برداشت اخیر</div>
                <div className="edit-card-body">
                  <table className="entity-table">
                    <thead>
                      <tr>
                        <th>تاریخ درخواست</th>
                        <th>مبلغ</th>
                        <th>کارمزد</th>
                        <th>خالص</th>
                        <th>وضعیت</th>
                        <th>بانک</th>
                        <th>روش واریز</th>
                        <th>کد پیگیری</th>
                      </tr>
                    </thead>
                    <tbody>
                      {storeStmt.withdrawals.map((w) => (
                        <tr key={w.id}>
                          <td>{formatJalali(w.requestedAt)}</td>
                          <td style={{ fontWeight: 700 }}>{formatToman(w.amount)}</td>
                          <td>{formatToman(w.processingFee)}</td>
                          <td>{formatToman(w.netAmount)}</td>
                          <td>
                            <Chip size="small" variant="outlined" label={STATUS_LABELS[w.status] || w.status} />
                          </td>
                          <td>{w.bankName || '—'}</td>
                          <td>{w.payoutMode === 'zarinpal' ? 'واریز خودکار زرین‌پال' : w.payoutMode === 'manual' ? 'واریز دستی بانکی' : '—'}{w.payoutStatus ? <span style={{ display: 'block', fontSize: 10, color: 'var(--color-text-secondary)' }}>{w.payoutStatus}</span> : null}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{w.referenceId || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 🧾 چرا پول این فروشنده قابل برداشت نیست؟ */}
            {kind === 'store' && storeExplain && (
              <div className="edit-card" style={{ marginTop: 16 }}>
                <div className="edit-card-header">چرا موجودی این فروشنده قابل برداشت است/نیست؟ (ریز آیتم‌ها و فرمول)</div>
                <div className="edit-card-body" style={{ fontSize: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 12 }}>
                    {[
                      { l: 'قابل برداشت', v: storeExplain.balance.availableBalance, c: '#2e7d32' },
                      { l: 'در انتظار (گارانتی/تحویل)', v: storeExplain.balance.pendingBalance, c: '#b45309' },
                      { l: 'مسدود', v: storeExplain.balance.blockedAmount, c: '#6a1b9a' },
                      { l: 'قفل بابت بازپرداخت', v: storeExplain.balance.refundObligationsTotal, c: '#d32f2f' },
                      { l: 'کسری تأمین وجه', v: storeExplain.balance.refundShortfall, c: '#d32f2f' },
                      { l: 'بدهی', v: storeExplain.balance.debt, c: '#c62828' },
                      { l: 'استخر تأمین وجه', v: storeExplain.balance.fundingPool, c: '#1565c0' },
                    ].map((k) => (
                      <div key={k.l} style={{ padding: 10, background: 'var(--color-bg-default)', borderRadius: 6 }}>
                        <div style={{ color: 'var(--color-text-secondary)' }}>{k.l}</div>
                        <div style={{ fontWeight: 700, color: k.c }}>{formatToman(k.v)}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>فرمول قابل برداشت</div>
                    <table className="entity-table">
                      <tbody>
                        {storeExplain.breakdown.formula.filter((f) => !['pending', 'blocked', 'debt', 'refund_shortfall', 'pending_from_items'].includes(f.key)).map((f) => (
                          <tr key={f.key} style={f.op === '=' || f.op === 'clamp' ? { fontWeight: 700 } : {}}>
                            <td style={{ width: 40, color: f.op === '-' ? '#c62828' : f.op === '+' ? '#2e7d32' : 'inherit' }}>{f.op === 'clamp' ? '≥۰' : f.op}</td>
                            <td>{f.label}</td>
                            <td style={{ direction: 'ltr', textAlign: 'right' }}>{formatToman(f.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                    <Chip size="small" label={`همه (${storeExplain.breakdown.items.length})`} onClick={() => setExplainBucket('all')} color={explainBucket === 'all' ? 'primary' : 'default'} />
                    {Object.entries(BUCKET_UI).filter(([k]) => storeExplain.breakdown.items.some((i) => i.bucket === k)).map(([k, u]) => (
                      <Chip key={k} size="small" variant={explainBucket === k ? 'filled' : 'outlined'} label={`${u.label} (${storeExplain.breakdown.items.filter((i) => i.bucket === k).length}) — ${formatToman(storeExplain.breakdown.items.filter((i) => i.bucket === k).reduce((s, i) => s + i.amount, 0))}`} onClick={() => setExplainBucket(k)} sx={{ color: u.color, borderColor: u.color }} />
                    ))}
                  </div>
                  <table className="entity-table">
                    <thead>
                      <tr><th>سفارش</th><th>محصول</th><th>سهم فروشنده</th><th>وضعیت</th><th>دلیل</th><th>تحویل / آزادسازی</th></tr>
                    </thead>
                    <tbody>
                      {storeExplain.breakdown.items.filter((i) => explainBucket === 'all' || i.bucket === explainBucket).map((it) => {
                        const u = BUCKET_UI[it.bucket] || BUCKET_UI.excluded;
                        return (
                          <tr key={it.orderItemId}>
                            <td style={{ fontFamily: 'monospace' }}>#{it.orderNumber}</td>
                            <td>{it.productTitle}{it.quantity > 1 ? ` ×${it.quantity}` : ''}</td>
                            <td style={{ fontWeight: 700 }}>{formatToman(it.amount)}</td>
                            <td><span style={{ color: u.color, fontWeight: 600 }}>{u.label}</span></td>
                            <td style={{ maxWidth: 340 }}>
                              {it.reasonText}
                              {it.disputeStatus && <span style={{ color: '#6a1b9a' }}> · اختلاف: {it.disputeStatus}</span>}
                              {it.obligation && <span style={{ color: '#d32f2f' }}> · تعهد {formatToman(it.obligation.amount)} ({it.obligation.status})</span>}
                            </td>
                            <td style={{ whiteSpace: 'nowrap', color: 'var(--color-text-secondary)' }}>
                              <div>تحویل: {formatJalali(it.deliveredAt || undefined)}</div>
                              {it.releaseDate && <div>آزادسازی: {formatJalali(it.releaseDate)}{it.daysUntilRelease ? ` (${it.daysUntilRelease} روز)` : ''}</div>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {storeExplain.breakdown.obligations.length > 0 && (
                    <div style={{ marginTop: 10, color: '#d32f2f' }}>
                      تعهدهای باز: {storeExplain.breakdown.obligations.map((o) => `${formatToman(o.amount)} (سفارش #${o.orderNumber || '—'}، ${o.status === 'awaiting_funds' ? 'در انتظار تأمین وجه' : o.status})`).join(' · ')}
                    </div>
                  )}
                  {storeExplain.breakdown.penalties.length > 0 && (
                    <div style={{ marginTop: 6, color: '#c62828' }}>
                      جریمه‌ها: {storeExplain.breakdown.penalties.map((p) => `${formatToman(p.amount)} (${p.description || ''})`).join(' · ')}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 🧾 دفتر رویدادهای مالی فروشنده */}
            {kind === 'store' && explainStoreId && (
              <div className="edit-card" style={{ marginTop: 16 }}>
                <div className="edit-card-header">دفتر رویدادهای مالی فروشنده (تاریخچهٔ کامل با دلیل و عامل)</div>
                <div className="edit-card-body" style={{ fontSize: 12 }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                    <select value={eventsType} onChange={(e) => { setEventsType(e.target.value); setEventsPage(1); }} style={{ padding: '6px 8px' }}>
                      {EVENT_GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                    </select>
                    <input placeholder="جست‌وجو: شماره سفارش / کد برداشت / محصول" value={eventsSearch} onChange={(e) => setEventsSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') setEventsPage(1); }} style={{ padding: '6px 8px', minWidth: 260 }} />
                    <button className="action-btn" onClick={() => { setEventsPage(1); loadStoreEvents(explainStoreId, 1, eventsType, eventsSearch); }}>جست‌وجو</button>
                  </div>
                  {!storeEvents || storeEvents.rows.length === 0 ? (
                    <div style={{ color: 'var(--color-text-secondary)' }}>رویدادی ثبت نشده است (دفتر از ۱۷ شهریور ۱۴۰۵ فعال است).</div>
                  ) : (
                    <table className="entity-table">
                      <thead>
                        <tr><th>زمان</th><th>رویداد</th><th>اثر</th><th>مبلغ</th><th>شرح</th><th>مرجع</th><th>عامل</th></tr>
                      </thead>
                      <tbody>
                        {storeEvents.rows.map((ev) => {
                          const ef = EFFECT_UI[ev.effect] || EFFECT_UI.info;
                          const actor = ev.actorType === 'admin' ? 'ادمین' : ev.actorType === 'seller' ? 'فروشنده' : ev.actorType === 'buyer' ? 'خریدار' : 'سیستم';
                          const actorName = typeof ev.actorId === 'object' && ev.actorId ? (ev.actorId as any).name : ev.actorName;
                          return (
                            <tr key={ev._id}>
                              <td style={{ whiteSpace: 'nowrap' }}>{moment(ev.createdAt).format('jYYYY/jMM/jDD HH:mm')}</td>
                              <td style={{ fontWeight: 700 }}>{ev.title || storeEvents.labels.types[ev.type] || ev.type}</td>
                              <td><span style={{ color: ef.color, fontWeight: 600 }}>{ef.label}</span></td>
                              <td style={{ color: ef.color, fontWeight: 700, direction: 'ltr', textAlign: 'right' }}>{ev.amount > 0 ? `${ef.sign} ${formatToman(ev.amount)}` : '—'}</td>
                              <td style={{ maxWidth: 420, lineHeight: 1.7 }}>
                                {ev.description}
                                {(ev.bucketFrom || ev.bucketTo) && <div style={{ color: 'var(--color-text-secondary)' }}>{storeEvents.labels.buckets[ev.bucketFrom || ''] || ev.bucketFrom || '—'} ← {storeEvents.labels.buckets[ev.bucketTo || ''] || ev.bucketTo || '—'}</div>}
                              </td>
                              <td style={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 11 }}>
                                {ev.orderNumber && <div>#{ev.orderNumber}</div>}
                                {ev.withdrawalReferenceId && <div>{ev.withdrawalReferenceId}</div>}
                                {ev.productTitle && <div style={{ fontFamily: 'inherit', color: 'var(--color-text-secondary)' }}>{ev.productTitle}</div>}
                              </td>
                              <td>{actor}{actorName ? ` (${actorName})` : ''}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  {storeEvents && storeEvents.pagination.pages > 1 && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
                      <button className="action-btn" disabled={eventsPage <= 1} onClick={() => setEventsPage(eventsPage - 1)}>قبلی</button>
                      <span>صفحه {eventsPage} از {storeEvents.pagination.pages}</span>
                      <button className="action-btn" disabled={eventsPage >= storeEvents.pagination.pages} onClick={() => setEventsPage(eventsPage + 1)}>بعدی</button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </ProtectedRoute>
  );
};

export default FinanceStatements;
